import { FileText, ShieldCheck } from 'lucide-react';

import { useAccessLog } from '../hooks/use-access-log';
import { formatRelativeTime } from '../lib/format-relative-time';
import { Badge } from './Badge';
import { Button } from './Button';

import type { AccessLogAction, AccessLogEntry } from '../lib/types';

const ACTION_BADGE: Record<AccessLogAction, { variant: 'success' | 'info' | 'warning' | 'error'; label: string }> = {
  approved: { variant: 'success', label: 'Approved' },
  accessed: { variant: 'info', label: 'Accessed' },
  injected: { variant: 'warning', label: 'Injected' },
  revoked: { variant: 'error', label: 'Revoked' },
  enabled: { variant: 'success', label: 'Enabled' },
  disabled: { variant: 'warning', label: 'Disabled' },
};

function AccessLogSkeleton() {
  return (
    <div data-testid="access-log-skeleton" className="flex flex-col gap-sd-1 p-sd-3 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-sd-3 px-sd-3 py-sd-2">
          <div className="w-16 h-5 bg-sd-border/30 rounded-sd-sm" />
          <div className="w-28 h-4 bg-sd-border/20 rounded" />
          <div className="flex-1 h-4 bg-sd-border/20 rounded" />
          <div className="w-20 h-4 bg-sd-border/20 rounded" />
          <div className="w-16 h-4 bg-sd-border/20 rounded" />
        </div>
      ))}
    </div>
  );
}

function AccessLogRow({ entry }: { entry: AccessLogEntry }) {
  const badge = ACTION_BADGE[entry.action];
  return (
    <div
      role="row"
      className="flex items-center gap-sd-3 px-sd-3 py-sd-2 border-b border-sd-border/20 text-sd-sm hover:bg-sd-surface-elevated/40 transition-colors duration-100"
    >
      <div className="shrink-0 w-20">
        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
      </div>
      <span className="min-w-0 flex-1 truncate text-sd-text">
        {entry.credential_name ?? entry.credential_service ?? entry.credential_id}
      </span>
      <span className="min-w-0 max-w-[160px] truncate text-sd-text-secondary hidden @md:inline">
        {entry.plan_title ?? (entry.plan_id ? entry.plan_id : '\u2014')}
      </span>
      <span className="w-24 truncate text-sd-text-secondary hidden @lg:inline">
        {entry.actor_name ?? '\u2014'}
      </span>
      <span className="w-20 text-sd-xs text-sd-text-secondary shrink-0 text-right">
        {formatRelativeTime(entry.created_at)}
      </span>
    </div>
  );
}

export interface AccessLogViewProps {
  enabled?: boolean;
}

export function AccessLogView({ enabled = true }: AccessLogViewProps) {
  const { entries, status, error, hasMore, loadMore, reload } = useAccessLog({ enabled });

  if (status === 'loading') {
    return <AccessLogSkeleton />;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center">
        <p className="text-sd-error mb-sd-3">{error ?? 'Failed to load access log'}</p>
        <Button variant="primary" onClick={reload}>Retry</Button>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div data-testid="access-log-empty" className="flex flex-col items-center justify-center flex-1 p-sd-4 text-center">
        <div className="w-12 h-12 rounded-sd-lg bg-sd-info/10 flex items-center justify-center mb-sd-3 sd-reveal-1">
          <ShieldCheck size={24} className="text-sd-info/60" />
        </div>
        <p className="text-sd-lg font-semibold text-sd-text mb-sd-1 sd-reveal-2">No access events yet</p>
        <p className="text-sd-sm text-sd-text-muted max-w-xs sd-reveal-3">
          Credential access events will appear here when plans are approved and credentials are used.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header row */}
      <div role="row" className="flex items-center gap-sd-3 px-sd-3 py-sd-1 border-b border-sd-border/30 text-sd-xs font-medium text-sd-text-secondary uppercase tracking-wider bg-sd-surface-elevated/40">
        <span className="w-20">Action</span>
        <span className="flex-1">Credential</span>
        <span className="max-w-[160px] hidden @md:inline">Plan</span>
        <span className="w-24 hidden @lg:inline">User</span>
        <span className="w-20 text-right">When</span>
      </div>

      {/* Rows */}
      <div role="table" aria-label="Access log" className="flex-1 overflow-y-auto">
        {entries.map((entry) => (
          <AccessLogRow key={entry.id} entry={entry} />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center py-sd-2 border-t border-sd-border/20">
          <Button variant="ghost" size="sm" onClick={loadMore}>
            <FileText size={14} className="mr-sd-1" /> Load more
          </Button>
        </div>
      )}
    </div>
  );
}
